/* eslint-disable react/prop-types */

import { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import moment from "moment";
import _ from "lodash";

import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Modal from "@mui/material/Modal";
import CloseIcon from "@mui/icons-material/Close";

import SuiBox from "components/SuiBox";
import SuiButton from "components/SuiButton";
import SuiSelect from "components/SuiSelect";
import SuiTypography from "components/SuiTypography";
import TimePicker from "@mui/lab/TimePicker";
import LocalizationProvider from "@mui/lab/LocalizationProvider";
import SuiDatePicker from "components/SuiDatePicker";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import TextField from "@mui/material/TextField";
import FormField from "components/FormField";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import Switch from "@mui/material/Switch";
import MediaPicker from "components/MediaPicker";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import { Popup, Media } from "api";

const SortableItem = ({ popup, onEditPopup, onDelete, currLang, toggleStatus }) => {
  const { t } = useTranslation();

  const style = {
    borderBottom: "1px solid #eee",
  };

  const cellStyle = {
    display: "flex",
    alignItems: "center",
    height: "100%",
  };

  const editPopup = () => onEditPopup(popup);
  const readPopup = (prop) => {
    if (prop === "content") {
      return popup[prop + currLang] || ""
    }
    return popup[prop];
  }

  return (
    <SuiBox py={0.1} style={style}>
      <Grid container spacing={2}>
        <Grid item xs={1} onClick={editPopup}>
          <SuiBox />
        </Grid>
        <Grid item xs={5} onClick={editPopup}>
          <SuiBox p={1}>
            <SuiBox>
              {popup.imageOrText === 0 && (
                <img
                  style={{ maxWidth: "100%", borderRadius: 8, border: "3px solid #eee" }}
                  src={_.get(popup, "medium.s3ObjKey.Location", "")}
                  alt=""
                />
              )}
              {popup.imageOrText === 1 && (readPopup("content"))}
            </SuiBox>
          </SuiBox>
        </Grid>
        <Grid item xs={3} onClick={editPopup}>
          <SuiBox sx={cellStyle}>
            <SuiTypography variant="button">
              {moment(popup.createdAt).format("YYYY-MM-DD hh:mm:ss A")}
            </SuiTypography>
          </SuiBox>
        </Grid>
        <Grid item xs={2}>
          <SuiBox sx={cellStyle}>
            <Switch
              checked={popup.status === "active"}
              onChange={(v) => {
                toggleStatus(v.currentTarget.checked);
              }}
            />
            <SuiBox ml={1}>
              <SuiTypography variant="button">
                {t(popup.status === "active" ? "Active" : "Inactive")}
              </SuiTypography>
            </SuiBox>
          </SuiBox>
        </Grid>
        <Grid item xs={1}>
          <SuiBox
            sx={cellStyle}
            display="inline"
            p={1}
            style={{ cursor: "pointer" }}
            onClick={() => {
              onDelete(popup);
            }}
          >
            <CloseIcon />
          </SuiBox>
        </Grid>
      </Grid>
    </SuiBox>
  );
};

function EditPopup() {
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(true);
  const [, setLastUpdate] = useState(new Date());
  const [popups, setPopups] = useState([]);
  const [editingPopup, setEditingPopup] = useState({});
  const [editModalOpened, setEditModalOpened] = useState(false);
  const [currLang, setCurrLang] = useState("SC");
  const timePickerRef = useRef(null);
  const [scheduleDate, setScheduleDate] = useState(moment().add(2, "hours").format("YYYY-MM-DD"));
  const [scheduleTime, setScheduleTime] = useState(moment().add(2, "hours").format("HH:mm:00"));

  const getCurrLangLabel = () => {
    if (currLang === "TC") {
      return t("Traditional Chinese");
    }
    if (currLang === "SC") {
      return t("Simplified Chinese");
    }
    return t("English");
  };

  const loadData = async () => {
    setIsLoading(true);
    try {
      const params = { pageSize: 1000 };
      const rsp = await Popup.getAll(params);
      const { data } = rsp;
      setPopups(data.results);
    } catch (err) {
      console.error(err);
    }
    setIsLoading(false);
  };

  const saveEditingPopup = async (data) => {
    const popup = { ...data };
    let error;
    if (popup.imageOrText === 0 && !popup.mediaId) {
      error = "Missing image";
    } else if (popup.imageOrText === 1 && !(popup.content || popup.contentSC || popup.contentTC)) {
      error = "Missing text";
    } else if (scheduleDate === "Invalid date" || scheduleTime === "Invalid date") {
      error = "Invalid date entered";
    }
    if (error) {
      Swal.fire(t(error));
    } else {
      popup.publishedDate = moment(`${scheduleDate} ${scheduleTime}`).format("YYYY-MM-DD HH:mm:ss");
      if (!popup.id) {
        popup.status = "inactive";
        await Popup.create(popup);
        loadData();
      } else {
        await Popup.update(popup);
        popups.forEach((p, idx) => {
          popups[idx]["status"] = "inactive";
        })
        popups[popups.findIndex((b) => b.id === popup.id)] = popup;
        setPopups([...popups]);
      }
      setEditModalOpened(false);
      setLastUpdate(new Date());
    }
  };

  const readEditingPopup = (prop) => {
    if (prop === "content") {
      return editingPopup[prop + currLang] || ""
    }
    return editingPopup[prop];
  }
  const updateEditingPopup = async (prop, value, save = false) => {
    const n = { ...editingPopup };
    if (prop === "content") {
      n[prop + currLang] = value;
    } else {
      n[prop] = value;
    }
    setEditingPopup(n);
    if (save) {
      await saveEditingPopup(n);
    }
  };

  useEffect(async () => {
    if (editingPopup.mediaId && _.get(editingPopup, "medium.id") !== editingPopup.mediaId) {
      const { data } = await Media.get(editingPopup.mediaId);
      updateEditingPopup("medium", data);
    }
  }, [editingPopup])

  const editPopup = (popup) => {
    setEditingPopup(popup);
    const result = moment(
      popup.publishedDate || moment().add(1, "day").hours(0).minutes(0).seconds(0),
      "YYYY-MM-DD HH:mm:ss"
    );
    setScheduleDate(moment(result).format("YYYY-MM-DD"));
    setScheduleTime(moment(result).format("HH:mm:00"));
    setEditModalOpened(true);
  };

  const deletePopup = async (popup) => {
    const result = await Swal.mixin({
      customClass: {
        confirmButton: "button button-success",
        cancelButton: "button button-error",
      },
      buttonsStyling: false,
    }).fire({
      title: t("Confirm to delete?"),
      showCancelButton: true,
      confirmButtonText: t("Yes"),
      cancelButtonText: t("No"),
    });
    if (result.isConfirmed) {
      try {
        await Popup.del(popup.id);
        loadData();
      } catch (e) {
        console.error(e);
      }
    }
  };

  const handleScheduleChange = (date, time) => {
    const result = moment(`${date} ${time}`, "YYYY-MM-DD HH:mm:ss");
    setScheduleDate(moment(result).format("YYYY-MM-DD"));
    setScheduleTime(moment(result).format("HH:mm:00"));
  };

  useEffect(() => {
    loadData();
  }, []);

  return (
    <>
      <Helmet>
        <title>{t("Popups")}</title>
      </Helmet>
      <DashboardLayout>
        <DashboardNavbar />
        <SuiBox my={1.5} width="100%">
          <SuiBox width="100%">
            <Card>
              <SuiBox p={3} lineHeight={1} borderBottom="1px solid #eee">
                <SuiBox>
                  <SuiBox
                    py={1}
                    style={{
                      float: "right",
                      minWidth: 200,
                    }}
                  >
                    <SuiSelect
                      placeholder="Choose a type"
                      value={{
                        label: getCurrLangLabel(),
                        value: currLang,
                      }}
                      options={[
                        {
                          label: t("Traditional Chinese"),
                          value: "TC",
                        },
                        {
                          label: t("Simplified Chinese"),
                          value: "SC",
                        },
                        {
                          label: t("English"),
                          value: "",
                        },
                      ]}
                      onChange={(option) => {
                        setCurrLang(option.value);
                      }}
                    />
                  </SuiBox>
                  <SuiTypography variant="h5">{t("Popups")}</SuiTypography>
                  <SuiTypography variant="button" fontWeight="regular" color="text">
                    {t("Edit")}
                  </SuiTypography>
                </SuiBox>
              </SuiBox>
              {isLoading ? (
                <>
                  <br />
                  <br />
                  <br />
                  <br />
                  <br />
                  <br />
                  <br />
                  <br />
                  <br />
                  <br />
                </>
              ) : (
                <>
                  <SuiBox px={3}>
                    <FormField label={t("Title")} disabled value={t("Homepage Popup")} />
                  </SuiBox>
                  <SuiBox py={1} px={3} style={{ background: "#eee" }}>
                    <SuiTypography variant="button">{t("Popup")}</SuiTypography>
                  </SuiBox>
                  <SuiBox>
                    <SuiBox
                      py={2}
                      borderBottom="2px solid #eee"
                      style={{ fontSize: 14, fontWeight: "bold" }}
                    >
                      <Grid container spacing={2}>
                        <Grid item xs={1} />
                        <Grid item xs={5}>
                          <SuiTypography variant="button" style={{ color: "#aaa" }}>
                            {t("Image")}
                          </SuiTypography>
                        </Grid>
                        <Grid item xs={3}>
                          <SuiTypography variant="button" style={{ color: "#aaa" }}>
                            {t("Created On")}
                          </SuiTypography>
                        </Grid>
                        <Grid item xs={2}>
                          <SuiTypography variant="button" style={{ color: "#aaa" }}>
                            {t("Status")}
                          </SuiTypography>
                        </Grid>
                        <Grid item xs={1} />
                      </Grid>
                    </SuiBox>
                    {popups.map((popup) => (
                      <SortableItem
                        key={popup.id}
                        id={popup.id}
                        popup={popup}
                        onEditPopup={editPopup}
                        onDelete={deletePopup}
                        currLang={currLang}
                        toggleStatus={(isActive) => {
                          setEditingPopup(popup);
                          updateEditingPopup("status", isActive ? "active" : "inactive");
                          saveEditingPopup({ ...popup, ...{ status: isActive ? "active" : "inactive" } })
                        }}
                      />
                    ))}
                    <Grid container spacing={2}>
                      <Grid item xs={1} />
                      <Grid item xs={11}>
                        <SuiBox py={2}>
                          <SuiTypography
                            onClick={() => {
                              editPopup({
                                imageOrText: 0,
                              });
                              setEditModalOpened(true);
                            }}
                            variant="button"
                            style={{
                              color: "#aaa",
                              textDecoration: "underline",
                              cursor: "pointer",
                            }}
                          >
                            {t("Add Popup")}
                          </SuiTypography>
                        </SuiBox>
                      </Grid>
                    </Grid>
                  </SuiBox>
                </>
              )}
            </Card>
          </SuiBox>
        </SuiBox>
        <Footer />
      </DashboardLayout>

      <Modal
        open={editModalOpened}
        onClose={(event, reason) => {
          if (reason === "backdropClick") {
            setEditModalOpened(false);
          }
        }}
      >
        <SuiBox
          style={{
            position: "absolute",
            top: "25%",
            left: "50%",
            transform: "translate(-50%, -25%)",
            width: "70%",
            height: "70%",
          }}
        >
          <Card>
            <SuiBox px={2} py={3} borderBottom="1px solid #ccc">
              {t("New Popup")}
            </SuiBox>
            <SuiBox mb={.5} mt={2}>
              <FormControl>
                <SuiBox ml={2.5}>
                  <SuiTypography component="label" variant="caption" fontWeight="bold">
                    {t("Content Types")}
                  </SuiTypography>
                  <SuiBox ml={1.5}>
                    <RadioGroup name="radio-buttons-group" row>
                      <FormControlLabel
                        onChange={() => {
                          updateEditingPopup("imageOrText", 0);
                        }}
                        control={<Radio checked={editingPopup.imageOrText === 0} />}
                        label={
                          <SuiTypography component="label" variant="caption" fontWeight="bold">
                            {t("Image")}
                          </SuiTypography>
                        }
                      />
                      <SuiBox ml={3}>
                        <FormControlLabel
                          onChange={() => {
                            updateEditingPopup("imageOrText", 1);
                          }}
                          control={<Radio checked={editingPopup.imageOrText === 1} />}
                          label={
                            <SuiTypography component="label" variant="caption" fontWeight="bold">
                              {t("Text")}
                            </SuiTypography>
                          }
                        />
                      </SuiBox>
                    </RadioGroup>
                  </SuiBox>
                </SuiBox>
              </FormControl>
            </SuiBox>
            <SuiBox p={2}>
              {editingPopup.imageOrText === 0 && (
                <MediaPicker
                  onChange={(v) => {
                    updateEditingPopup("mediaId", v ? v.id : v);
                  }}
                  defaultSelectedMediaId={_.get(editingPopup, "medium.id", null)}
                />
              )}
              {editingPopup.imageOrText === 1 && (
                <FormField
                  label={t("Text")}
                  value={readEditingPopup("content")}
                  multiline
                  minRows={5}
                  onChange={(e) => {
                    updateEditingPopup("content", e.target.value);
                  }}
                />
              )}
              <FormField
                label={t("Publish Date")}
                customInput={
                  <SuiBox>
                    <SuiDatePicker
                      value={moment(scheduleDate).format("YYYY-MM-DD")}
                      input={{
                        icon: {
                          component: "event",
                          direction: "right",
                        },
                      }}
                      onChange={(...vars) => {
                        handleScheduleChange(vars[1], scheduleTime);
                      }}
                    />
                    <SuiTypography component="label" variant="caption" fontWeight="bold">
                      {t("Publish Time")}
                    </SuiTypography>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <TimePicker
                        inputRef={timePickerRef}
                        value={moment(scheduleTime, ["HH:mm:00"])}
                        onChange={(newValue) => {
                          handleScheduleChange(
                            scheduleDate,
                            moment(newValue).format("HH:mm:00")
                          );
                        }}
                        renderInput={(params) => (
                          <TextField {...params} style={{ width: "100%" }} />
                        )}
                        OpenPickerButtonProps={{
                          style: {
                            position: "absolute",
                            right: 10,
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </SuiBox>
                }
              />
              <SuiBox display="flex" justifyContent="flex-end">
                <SuiBox>
                  <SuiButton
                    onClick={() => {
                      setEditModalOpened(false);
                    }}
                  >
                    {t("Cancel")}
                  </SuiButton>
                </SuiBox>
                <SuiBox ml={2}>
                  <SuiButton
                    variant="gradient"
                    color="info"
                    onClick={() => {
                      saveEditingPopup(editingPopup);
                    }}
                  >
                    {t(editingPopup.id ? "Confirm" : "Create")}
                  </SuiButton>
                </SuiBox>
              </SuiBox>
            </SuiBox>
          </Card>
        </SuiBox>
      </Modal>
    </>
  );
}

export default EditPopup;
